import { UpdateDocumentParams, UploadDocumentParams } from '../../api/documents/types'
import { documentFields } from '../../common/forms/documents/fields'

interface UploadFormValues {
    document_project_internalAppId?: string | number
    document_property_internalAppId?: string | number
    document_contact_id?: string | number
    document_contact_internalAppId?: string | number
    document_type_id?: string | number
    document_creation_date?: string | null
    document_validity_date?: string | null
    file_sync?: boolean | number
}

interface FormDataParams {
    file: File
    values: UploadFormValues
    parentId?: string | number
}

export const buildFormData = ({ file, values, parentId }: FormDataParams): UploadDocumentParams => {
    const {
        document_property_internalAppId: propertyId,
        document_project_internalAppId: projectId,
        document_contact_id: contactId,
        document_contact_internalAppId: contactInternalAppId,
    } = values

    const formData = new FormData()
    formData.append('file', file)

    const uploadEntities = [
        { module: 'property', id: propertyId, url: 'property/document' },
        { module: 'project', id: projectId, url: 'project/document' },
        // unlike property/project (plain inputs, always internalAppId), the contact field is an
        // AsyncSelectInput whose own value is the raw contact db id — document_contact_internalAppId
        // carries the real internalAppId separately; fall back to contactId only when it's already
        // correct (entity-scoped pages prefill it with internalAppId directly, see documentValidation)
        { module: 'contact', id: contactInternalAppId ?? contactId, url: 'contact/document' },
    ]

    const entity = uploadEntities.find(({ id }) => id)
    const uploadUrl = entity?.url ?? ''

    if (entity) {
        formData.append('module', entity.module)
        formData.append('parent_internal_app_id', String(entity.id))
    }

    // only entity edit pages have a route parentId — the global list has none
    if (parentId) formData.append('parent', String(parentId))

    formData.append('collection', 'document')
    formData.append(documentFields.DOCUMENT_CREATION_DATE, values.document_creation_date ?? '')
    formData.append(documentFields.DOCUMENT_VALIDITY_DATE, values.document_validity_date ?? '')
    formData.append(documentFields.DOCUMENT_TYPE, String(values.document_type_id ?? ''))
    formData.append(documentFields.FILE_SYNC, values.file_sync ? '1' : '0')

    return { formData, uploadUrl }
}

interface UpdateFormValues {
    id?: string | number
    document_type_id?: string | number
    document_creation_date?: string | null
    document_validity_date?: string | null
    file_description?: string
    file_sync?: boolean | number
}

interface DocumentParentData {
    parent?: string | number
    parentElement?: { element?: { internalAppId?: string | number } }
}

interface UpdatePayloadParams {
    values: UpdateFormValues
    documentData?: DocumentParentData
}

export const buildUpdatePayload = ({ values, documentData }: UpdatePayloadParams): UpdateDocumentParams => ({
    id: values.id as string | number,
    document_type_id: values.document_type_id,
    document_creation_date: values.document_creation_date,
    document_validity_date: values.document_validity_date,
    file_description: values.file_description,
    file_sync: values.file_sync,
    parent: documentData?.parent,
    parentInternalAppId: documentData?.parentElement?.element?.internalAppId,
})
